import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useMemo, useRef } from "react";
import styles from "./TgImagesPreviewThumbstrip.module.scss";

type PaginationItem = number | "...";

type Props = {
	images: any[];
	selectedIndex: number;
	setSelectedIndex: (index: number) => void;
	canPrev: boolean;
	canNext: boolean;
	goPrev: () => void;
	goNext: () => void;
	paginationItems: PaginationItem[];
	isLoadingCaptions: boolean;
	prevLabel: string;
	nextLabel: string;
	showThumbLoaders?: boolean;
};

export const TgImagesPreviewThumbstrip: React.FC<Props> = ({
	images,
	selectedIndex,
	setSelectedIndex,
	canPrev,
	canNext,
	goPrev,
	goNext,
	paginationItems,
	isLoadingCaptions,
	prevLabel,
	nextLabel,
	showThumbLoaders = true,
}) => {
	const thumbStripRef = useRef<HTMLDivElement | null>(null);
	const selected = useMemo(() => images[selectedIndex], [images, selectedIndex]);

	return (
		<Box className={styles.previewRow}>
			<Box className={styles.previewColumn}>
				<Box className={styles.preview}>
					{selected ? <img src={selected.thumbnail_url || selected.image_url} /> : null}
					{isLoadingCaptions ? (
						<Box className={styles.loadingOverlay}>
							<CircularProgress color="primary" size={28} />
						</Box>
					) : null}
				</Box>
				<Box className={styles.numbersNav}>
					<span
						className={`${styles.navBtn} ${!canPrev ? styles.disabled : ""}`}
						onClick={() => {
							if (canPrev) goPrev();
						}}
					>
						<KeyboardArrowLeftIcon sx={{ fontSize: 12, width: 12, height: 12, color: "currentColor" }} />
						&nbsp;{prevLabel}
					</span>
					{paginationItems.map((item, idx) =>
						item === "..." ? (
							<span key={`e-${idx}`} className={styles.ellipsis}>
								…
							</span>
						) : (
							<span
								key={item}
								className={`${styles.num} ${item === selectedIndex ? styles.active : ""}`}
								onClick={() => setSelectedIndex(item as number)}
							>
								{(item as number) + 1}
							</span>
						),
					)}
					<span
						className={`${styles.navBtn} ${!canNext ? styles.disabled : ""}`}
						onClick={() => {
							if (canNext) goNext();
						}}
					>
						{nextLabel}&nbsp;
						<KeyboardArrowRightIcon sx={{ fontSize: 12, width: 12, height: 12, color: "currentColor" }} />
					</span>
				</Box>
			</Box>

			<Box className={styles.thumbStrip}>
				<Box className={styles.thumbScroll} ref={thumbStripRef}>
					<Box className={styles.thumbList}>
						{images.map((img, idx) => (
							<Box
								key={img.id}
								className={`${styles.thumbItem} ${idx === selectedIndex ? styles.thumbItemActive : ""}`}
								onClick={() => setSelectedIndex(idx)}
							>
								{showThumbLoaders && !img.caption ? (
									<Box className={styles.thumbItemLoader}>
										<CircularProgress size={16} className={styles.thumbLoader} />
									</Box>
								) : null}
								{idx === selectedIndex ? <Box className={styles.thumbOverlay} /> : null}
								<img src={img.thumbnail_url || img.image_url} />
							</Box>
						))}
					</Box>
				</Box>
				<Box
					className={styles.downBar}
					onClick={() => {
						const el = thumbStripRef.current;
						if (!el) return;
						el.scrollBy({ top: el.clientHeight, behavior: "smooth" });
					}}
				>
					<KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
				</Box>
			</Box>
		</Box>
	);
};

export default TgImagesPreviewThumbstrip;
